import React, { useEffect, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import { fetchPhotoBlob } from "../lib/api";

// AuthedImage — fetches /api/photos/:id with the Bearer token via axios,
// turns the binary blob into an object URL, and assigns it to <img src>.
// Cleans the object URL on unmount to avoid leaks.
//
// Props:
//   photoId        : string | null — when null/empty, renders a placeholder
//   alt            : string
//   className      : string (applied to the <img>)
//   onClick        : optional handler (e.g. open lightbox)
//   placeholderClass : optional class for the fallback box
//
// On a 404/auth error, shows an "image off" icon instead of breaking.
export default function AuthedImage({
    photoId, alt = "", className = "", onClick,
    placeholderClass = "",
}) {
    const [url, setUrl] = useState(null);
    const [state, setState] = useState("idle"); // idle | loading | ok | err
    useEffect(() => {
        if (!photoId) { setState("idle"); setUrl(null); return; }
        let alive = true;
        let blob;
        setState("loading");
        fetchPhotoBlob(photoId)
            .then((u) => {
                if (!alive) { URL.revokeObjectURL(u); return; }
                blob = u; setUrl(u); setState("ok");
            })
            .catch(() => { if (alive) { setState("err"); setUrl(null); } });
        return () => {
            alive = false;
            if (blob) URL.revokeObjectURL(blob);
        };
    }, [photoId]);

    if (state === "ok" && url) {
        return (
            <img
                src={url} alt={alt}
                onClick={onClick}
                className={`${className} ${onClick ? "cursor-zoom-in" : ""}`}
                data-testid="authed-image"
                draggable={false}
            />
        );
    }
    return (
        <div
            className={`flex items-center justify-center bg-slate-800/60 text-slate-500 ${placeholderClass || className}`}
            data-testid={state === "loading" ? "authed-image-loading" : "authed-image-fallback"}
        >
            {state === "loading"
                ? <Loader2 className="animate-spin" size={16} />
                : <ImageOff size={16} />}
        </div>
    );
}

// Lightbox — full-screen modal showing a single AuthedImage.
// Closes on backdrop click or Escape.
export function PhotoLightbox({ photoId, onClose, caption }) {
    useEffect(() => {
        if (!photoId) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [photoId, onClose]);
    if (!photoId) return null;
    return (
        <div
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="photo-lightbox"
        >
            <div className="max-w-5xl w-full max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <AuthedImage
                    photoId={photoId}
                    alt=""
                    className="max-h-[85vh] w-auto object-contain rounded-lg shadow-2xl"
                    placeholderClass="w-[60vw] h-[60vh] rounded-lg"
                />
                {caption && (
                    <div className="text-xs text-slate-300 text-center" data-testid="lightbox-caption">{caption}</div>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="lightbox-close"
                    className="px-4 h-9 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm border border-slate-700"
                >
                    Cerrar
                </button>
            </div>
        </div>
    );
}
